"use client";

import { useState } from "react";
import { TextAreaField } from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import { IdentifierResults } from "@/components/tools/ciphers-misc/cipher-identifier-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { identify } from "@/lib/tool-kit/ciphers-misc/cipher-identifier";

/** English strings. A translated content file overrides these through `ui`. */
export const CIPHER_IDENTIFIER_STRINGS = {
  input: "Text to identify",
  inputHint:
    "Paste cipher text, numbers or symbols. Spaces and line breaks are fine. A few hundred letters give far better guesses than a few words. Nothing leaves your browser.",
  inputPlaceholder: "Paste the mystery text here",
  sampleLabel: "Try a sample:",
  sampleCaesar: "Caesar shift",
  sampleVigenere: "Repeating key",
  sampleSubstitution: "Substitution",
  sampleTransposition: "Transposition",
  clear: "Clear",
  textCaesar:
    "TLLAT LHAAO LVSKZ AVULI YPKNL DOLUA OLJSV JRZAY PRLZT PKUPN OAHUK IYPUN AOLSL AALYZ DPAOF VBALS SUVVU LDOLY LFVBH YLNVP UN",
  textVigenere:
    "TXUGN EVGHU FRUJR CWMZY JEOYA ZAXSQ RIPHU LXMGV YKXSZ LRUBC ZWESF DMABB QESCB OJAFG FRQAH DXNSV YAMBG ZJMKV QITCJ PZQFY TXFZR VRAKA ELQTR PPUBT DSDJV PAECS DYOVN XEZAN JFQCA SMETV CWFSA EIDWA REZSV RLNCH CLACQ ELUGG CYFVV DWAKR WPRWK PHUBG SIYWA OWATG SIEIE CSGBQ TRSTN XMXWR D",
  textSubstitution:
    "VITFO FZITE GXKLT GYIXD QFTCT FZLOZ WTEGD TLFTE TLLQK NYGKG FTHTG HSTZG ROLLG SCTZI THGSO ZOEQS WQFRL VIOEI IQCTE GFFTE ZTRZI TDVOZ IQFGZ ITKQF RZGQL LXDTQ DGFUZ ITHGV TKLGY ZITTQ KZIZI TLTHQ KQZTQ FRTJX QSLZQ ZOGFZ GVIOE IZITS QVLGY FQZXK TTFZO ZSTZI TD",
  textTransposition:
    "CMHLE ROENW GCLVL LNNNU AONRU TTSOO TGWDL UIESH TPOEL ALESM ESMYA SGNVR IDOLN PEIEY AIGIT EROOE IMPRE NNTIG ATCLR ONEET ENHRI HUHIO LSIAO TLTLA DETEA EYATF HWRDL IAOEA EMHOR SHNTO MYYSD HPIAI RMSEO TUABA TNEWR RTO",
  empty: "Paste some text above to see which kind of cipher it resembles.",
  shortText:
    "This is a short text. Letter statistics need a few dozen letters at least, so every guess based on them is marked as no more than possible.",
  resultsHeading: "Most likely, in order",
  resultsHint:
    "These are guesses from simple measurements, not proof. A strong signal means the evidence fits that family and little else; try the linked tool to confirm.",
  tryNext: "Try:",
  levels: {
    strong: "Strong signal",
    possible: "Possible",
    weak: "Weak hint",
  },
  families: {
    english: {
      name: "Plain English",
      summary:
        "The text does not look enciphered at all, or it is a very simple code that keeps the language.",
    },
    shift: {
      name: "Caesar shift (including ROT13)",
      summary:
        "Every letter has been moved the same number of places along the alphabet.",
    },
    atbash: {
      name: "Atbash",
      summary: "The alphabet reversed, so A becomes Z, B becomes Y and so on.",
    },
    affine: {
      name: "Affine cipher",
      summary:
        "Each letter's position is multiplied by a number and then shifted.",
    },
    substitution: {
      name: "Simple substitution",
      summary:
        "Each letter is replaced by another letter, always the same one. Solved by frequency analysis and guessing words.",
    },
    transposition: {
      name: "Transposition",
      summary:
        "The letters are the original ones in a different order, as in a rail fence, columnar, scytale or route cipher.",
    },
    periodic: {
      name: "Repeating-key cipher",
      summary:
        "Several shifted alphabets used in turn, as in the Vigenère, Beaufort and Porta ciphers. The key length is the first thing to find.",
    },
    digraph: {
      name: "Digraph cipher",
      summary:
        "Letters enciphered in pairs, as in the Playfair, Four-Square and 2 × 2 Hill ciphers.",
    },
    flat: {
      name: "No pattern: autokey, fractionating, machine or one-time pad",
      summary:
        "The letters look random and no repeating key stands out. That fits an autokey cipher, a fractionating cipher such as Bifid, a rotor machine, a one-time pad or modern encryption. Without more text or a crib there is little to go on.",
    },
    adfgvx: {
      name: "ADFGVX or ADFGX cipher",
      summary:
        "Only six letters are used: the output of a Polybius square followed by a transposition.",
    },
    bacon: {
      name: "Bacon's cipher",
      summary:
        "Each letter is written as five two-way choices, as a, b or 0, 1 in groups of five.",
    },
    morse: {
      name: "Morse code",
      summary:
        "Dots and dashes, with spaces or slashes between letters and words.",
    },
    binary: {
      name: "Binary text",
      summary: "Characters written as eight bits each.",
    },
    a1z26: {
      name: "Letter numbers (A1Z26)",
      summary: "A = 1 through Z = 26.",
    },
    polybius: {
      name: "Polybius square",
      summary: "Each letter written as its row and column in a 5 × 5 grid.",
    },
    book: {
      name: "Book cipher or numeric code",
      summary:
        "Numbers that point to words or letters in a text both people hold. Without the book, little can be done except guessing it.",
    },
    phone: {
      name: "Phone keypad digits",
      summary:
        "Letters written as the keys of a telephone, either by repeated presses (multi-tap) or one digit each (T9).",
    },
    hex: {
      name: "Hexadecimal bytes",
      summary:
        "Bytes written in base 16. They may be plain text, or the output of a cipher such as XOR.",
    },
    "ascii-numbers": {
      name: "Decimal character codes",
      summary: "Characters written as their ASCII codes.",
    },
    base32: {
      name: "Base32",
      summary: "Bytes written with 32 characters, often used for secret keys.",
    },
    base64: {
      name: "Base64",
      summary:
        "Bytes written with 64 characters; not a cipher but an encoding.",
    },
    ascii85: {
      name: "Ascii85",
      summary: "Bytes written with 85 printable characters.",
    },
    braille: {
      name: "Braille",
      summary: "Unicode Braille cells.",
    },
    symbols: {
      name: "Symbol alphabet",
      summary:
        "Mostly symbols rather than letters or digits: perhaps a pictorial alphabet such as pigpen, dingbats or emoji.",
    },
    "other-script": {
      name: "Another alphabet",
      summary:
        "Letters that are not A–Z. The tools linked from this page work on the Latin alphabet.",
    },
  },
  reasons: {
    "english-pairs":
      "Its letter pairs are those of ordinary English, so this looks like plain text rather than a cipher.",
    "shift-reads":
      "Shifting every letter back by {shift} gives readable English: {preview}…",
    "atbash-reads":
      "Swapping each letter with its mirror in the alphabet (A with Z) gives readable English: {preview}…",
    "affine-reads":
      "The affine key a = {a}, b = {b} gives readable English: {preview}…",
    "ic-english":
      "The index of coincidence is {ic}, close to English (about 0.067): the letters are used unevenly, as in a plain language.",
    "letters-in-place":
      "The letter frequencies sit almost exactly where English puts them (distance {chi} per letter), so the letters have probably only been moved.",
    "letters-moved":
      "The letter frequencies are far from English (distance {chi} per letter) though just as uneven, so the letters have probably been replaced by other letters.",
    "ic-between":
      "The index of coincidence is {ic}, between English (about 0.067) and random letters (about 0.038), as when several alphabets are mixed.",
    "period-ic":
      "Dealing the letters into {length} columns gives columns whose average index of coincidence is {ic}, near English, as if each column were one shifted alphabet.",
    "kasiski-agrees":
      "Repeated three-letter groups are spaced by multiples of {factor} ({count} times), which agrees with that key length.",
    "even-length":
      "The text has an even number of letters ({letters}), as a digraph cipher needs.",
    "no-doubled-pairs":
      "None of its {pairs} letter pairs is a double such as AA. Playfair never makes one, and most other ciphers would by now.",
    "no-j": "The letter J never appears, as when I and J share a square.",
    "ic-flat":
      "The index of coincidence is {ic}, near that of random letters (about 0.038), and no repeating key length stands out.",
    "phone-runs":
      "Every group is one digit from 2 to 9 repeated, with 0 between words: multi-tap texting.",
    "phone-digits":
      "Only the digits 2 to 9 and 0 appear, which fits T9 keypad digits (one digit per letter).",
    "a1z26-range":
      "Every number lies between 1 and 26, one for each letter of the alphabet.",
    "polybius-pairs":
      "Every number has two digits, each from 1 to 5: a row and a column in a 5 × 5 square.",
    "polybius-run":
      "The digits are all between 1 and 5 and there is an even number of them: row and column pairs.",
    "ascii-range":
      "Every number lies between 32 and 126, the printable ASCII characters.",
    "book-numbers":
      "The numbers run up to {max}, beyond 26, so they point at positions rather than at letters.",
    "binary-bytes":
      "The text is groups of eight 0s and 1s: one byte per character.",
    "binary-digits":
      "The text is only 0s and 1s, in a total that could be whole bytes.",
    "bacon-bits":
      "Only 0s and 1s, in a total divisible by five: five-bit groups as in Bacon's cipher.",
    "bacon-letters":
      "Only two different letters appear, in a total divisible by five: Bacon's five-letter groups.",
    "braille-cells": "The text is made of Braille cell characters.",
    "morse-chars": "Only dots, dashes and separators appear.",
    "ascii85-markers":
      "The text begins with <~ and ends with ~>, the Ascii85 markers.",
    "hex-bytes": "Every group is two hexadecimal digits: one byte each.",
    "hex-run": "Only hexadecimal digits appear, and an even number of them.",
    "base32-padded":
      "Only A–Z and 2–7 appear, ending in = padding to a multiple of eight: Base32.",
    "base32-alphabet":
      "Only A–Z and 2–7 appear, with no spaces; Base32 uses exactly these characters.",
    "base64-padded":
      "The characters belong to the Base64 alphabet and end in = padding to a multiple of four.",
    "base64-alphabet":
      "Capitals, lower-case letters and digits are mixed with no spaces, as in Base64 text.",
    "adfgvx-letters":
      "Only {distinct} different letters appear, all from A, D, F, G, V and X, the letters of the ADFGVX cipher.",
    "other-script":
      "Many of the letters are not A–Z, so this may be another language or alphabet, or a transliteration.",
    "mostly-symbols": "Most of the characters are neither letters nor digits.",
  },
  statsHeading: "The measurements",
  colMeasure: "Measure",
  colValue: "Value",
  colMeaning: "What to compare it with",
  statCharacters: "Characters (without spaces)",
  statLetters: "Letters A–Z",
  statOther: "Other characters (without spaces)",
  statDistinct: "Different letters used",
  statIc: "Index of coincidence",
  statIcNote:
    "English text is about 0.067; random letters about 0.038; a simple substitution or a transposition keeps the English value.",
  statPairs: "English letter-pair score",
  statPairsNote:
    "Plain English usually scores 0.6 or more; shuffled or enciphered text much less.",
  statChi: "Letter-frequency distance from English",
  statChiNote:
    "Under about 0.6 the letters are where English puts them; far above that, they have been replaced.",
  statPeriods: "Best repeating-key lengths",
  periodItem: "{length} (index {ic})",
  statPeriodsNote:
    "The average index of coincidence when the text is dealt into that many columns; near 0.067 suggests the key length.",
  statKasiski: "Repeated groups: common spacing factors",
  kasiskiItem: "{factor} divides {count} gaps",
  statKasiskiNote:
    "Kasiski's test: repeated three-letter groups tend to be spaced by multiples of the key length.",
  linkLabels: {
    "letter-frequency-counter": "Letter Frequency Counter",
    "caesar-cipher": "Caesar Cipher Encoder & Decoder",
    "affine-cipher": "Affine Cipher",
    "atbash-cipher": "Atbash Cipher",
    "substitution-cipher": "Substitution Cipher Solver",
    "rail-fence-cipher": "Rail Fence Cipher",
    "columnar-transposition-cipher": "Columnar Transposition Cipher",
    "scytale-cipher": "Scytale Cipher",
    "route-cipher": "Route Cipher",
    "vigenere-cipher": "Vigenère Cipher Encoder & Decoder",
    "beaufort-cipher": "Beaufort Cipher",
    "porta-cipher": "Porta Cipher",
    "playfair-cipher": "Playfair Cipher",
    "four-square-cipher": "Four-Square Cipher",
    "hill-cipher": "Hill Cipher",
    "autokey-cipher": "Autokey Cipher",
    "bifid-cipher": "Bifid Cipher",
    "trifid-cipher": "Trifid Cipher",
    "one-time-pad": "One-Time Pad",
    "enigma-machine": "Enigma Machine Simulator",
    "adfgvx-cipher": "ADFGVX Cipher",
    "polybius-square-cipher": "Polybius Square Cipher",
    "nihilist-cipher": "Nihilist Cipher",
    "bacon-cipher": "Bacon Cipher",
    "morse-code-translator": "Morse Code Translator",
    "binary-code-translator": "Binary Code Translator",
    "a1z26-cipher": "Letters to Numbers (A1Z26) Converter",
    "book-cipher": "Book Cipher",
    "phone-keypad-cipher": "Phone Keypad Cipher",
    "hex-converter": "Text to Hex Converter",
    "xor-cipher": "XOR Cipher",
    "ascii-converter": "ASCII Code Converter",
    "base-encoding-converter": "Base32, Base58 and Ascii85 Converter",
    "text-encoder-decoder": "Text Encoder & Decoder",
    "braille-translator": "Braille Translator",
    "pigpen-cipher": "Pigpen Cipher",
  },
} as const;

export default function CipherIdentifierWidget() {
  const strings = useToolUi(CIPHER_IDENTIFIER_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState<string>(strings.textVigenere);
  const result = identify(input);

  const samples = [
    { label: strings.sampleCaesar, text: strings.textCaesar },
    { label: strings.sampleVigenere, text: strings.textVigenere },
    { label: strings.sampleSubstitution, text: strings.textSubstitution },
    { label: strings.sampleTransposition, text: strings.textTransposition },
  ];

  return (
    <div className="space-y-5">
      <TextAreaField
        hint={strings.inputHint}
        id="ci-input"
        label={strings.input}
        onChange={setInput}
        placeholder={strings.inputPlaceholder}
        prose={false}
        rows={7}
        value={input}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className={HINT}>{strings.sampleLabel}</span>
        {samples.map((sample) => (
          <button
            className={BUTTON_SECONDARY}
            key={sample.label}
            onClick={() => setInput(sample.text)}
            type="button"
          >
            {sample.label}
          </button>
        ))}
        <button
          className={BUTTON_SECONDARY}
          disabled={input.length === 0}
          onClick={() => setInput("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div aria-live="polite">
        <IdentifierResults fmt={fmt} result={result} strings={strings} />
      </div>
    </div>
  );
}
